import React, { useState } from 'react'
import { UserLock, UserPlus, UserMinus } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Separator } from 'components/ui/separator'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'components/ui/tooltip'
import PartnerPermissionsSheet from 'components/sheets/PartnerPermissionsSheet'
import Popup from 'components/Popup'
import PartnerReminderPopup from 'components/popups/PartnerReminderPopup'

export interface PartnerProjectCardCNProps {
	name: string
	title: string
	companyName: string
	partner: any
	isOwner: boolean
	isAccepted: boolean
	className?: string
	avatarUrl?: string
	handelRemindPartner?: Function
	handleUpdatePartner?: Function
	/**
	 * 'Full Access' / 'Read Only' badge. Only portfolios grant per-asset access
	 * levels today; omitting this renders nothing, so project and location
	 * partner cards are unchanged.
	 */
	accessLevelLabel?: string
	/** Omit to hide the remove action (non-owners). */
	handleRemovePartner?: Function
}

export const PartnerProjectCardCN = ({
	className = '',
	name,
	title,
	companyName,
	partner,
	isOwner,
	isAccepted,
	avatarUrl,
	handelRemindPartner,
	handleUpdatePartner,
	accessLevelLabel,
	handleRemovePartner,
}: PartnerProjectCardCNProps) => {
	const [sheetOpen, setSheetOpen] = useState(false)
	const [reInviteOpen, setReInviteOpen] = useState(false)

	const clip = (text: string) => (text && text.length > 25 ? text.slice(0, 22) + '...' : text || '')

	const handleClick = () => {
		if (isAccepted) {
			setSheetOpen(true)
		} else {
			setReInviteOpen(true)
		}
	}

	return (
		<>
			<div
				data-testid="PartnerProjectCardCN"
				className={`bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col overflow-hidden h-[260px] ${className}`}
			>
				{/* Banner */}
				<div className="h-16 w-full" style={{ background: 'linear-gradient(135deg, #0080fe 0%, #00b4d8 100%)' }} />

				{/* Avatar — overlaps the banner */}
				<div className="flex justify-center -mt-10">
					<Avatar className="h-24 w-24 shadow-sm">
						<AvatarImage src={avatarUrl} alt={name} />
						<AvatarFallback className="text-2xl font-semibold bg-gray-100 text-gray-600">
							{name ? name.charAt(0).toUpperCase() : '?'}
						</AvatarFallback>
					</Avatar>
				</div>

				{/* Info */}
				<div className="flex-1 flex flex-col items-center text-center px-4 pt-2 pb-3">
					<h3 className="text-sm font-semibold text-gray-900 max-w-full">{clip(name)}</h3>
					{isAccepted ? (
						<>
							{title && <p className="text-xs text-gray-500 max-w-full mt-0.5">{clip(title)}</p>}
							{companyName && <p className="text-xs text-gray-400 max-w-full mt-0.5">{clip(companyName)}</p>}
						</>
					) : (
						<p className="text-xs text-amber-600 max-w-full mt-0.5">Pending Partnership</p>
					)}
					{accessLevelLabel && (
						<span className="mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-secondary text-secondary-foreground">
							{accessLevelLabel}
						</span>
					)}
				</div>

				{/* Action */}
				<div className="mt-auto">
					<Separator className="bg-gray-200" />
					<div className="flex items-center justify-center px-4 py-3">
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type="button"
										title="Partner Permissions"
										className="inline-flex items-center justify-center w-10 h-10 rounded-full text-[#0080fe] hover:bg-gray-100 transition-colors"
										onClick={handleClick}
									>
										<UserLock className="w-6 h-6" />
									</button>
								</TooltipTrigger>
								<TooltipContent className="bg-white text-[#0080fe] shadow-lg text-sm border">
									Partner Permissions
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>

						{handleRemovePartner && (
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<button
											type="button"
											title="Remove Partner"
											className="inline-flex items-center justify-center w-10 h-10 rounded-full text-gray-400 hover:text-destructive hover:bg-gray-100 transition-colors"
											onClick={() => handleRemovePartner(partner)}
										>
											<UserMinus className="w-6 h-6" />
										</button>
									</TooltipTrigger>
									<TooltipContent className="bg-white text-destructive shadow-lg text-sm border">
										Remove Partner
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						)}
					</div>
				</div>
			</div>

			{/* Permissions Sheet */}
			<PartnerPermissionsSheet
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
				partner={partner}
				isOwner={isOwner}
				onUpdate={handleUpdatePartner}
			/>

			{/* Re-invite Popup (for non-accepted partners) */}
			{reInviteOpen && (
				<Popup title="Re-Invite Partner" openPopup={reInviteOpen} setOpenPopup={setReInviteOpen} icon={<UserPlus className="w-5 h-5" />} description="Send a new invitation to this partner">
					<PartnerReminderPopup partner={partner} remindPartner={handelRemindPartner} />
				</Popup>
			)}
		</>
	)
}

export default PartnerProjectCardCN
